import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "fg-cookie-consent";

export const getConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [external, setExternal] = useState(false);
  const [statistics, setStatistics] = useState(false);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
  }, []);

  const save = (consent) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...consent, date: new Date().toISOString() })
    );
    setVisible(false);
    // HINWEIS: Nicht-notwendige Skripte (z. B. Google Maps / Analytics)
    // erst nach Zustimmung laden — hier anhand von consent.external / consent.statistics prüfen.
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 inset-x-0 z-[100] bg-[#1F2933] text-white shadow-2xl border-t border-white/10"
      role="dialog"
      aria-label="Cookie-Einstellungen"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        <div className="flex items-start gap-4">
          <Cookie size={26} className="text-[#C9B79C] shrink-0 mt-1 hidden sm:block" />
          <div className="flex-1">
            <p className="font-heading font-semibold text-base">Wir respektieren Ihre Privatsphäre</p>
            <p className="mt-1 text-sm text-white/70 leading-relaxed">
              Diese Website verwendet Cookies. Notwendige Cookies sind für den
              Betrieb der Seite erforderlich. Weitere Cookies (z. B. für externe
              Medien oder Statistik) werden nur mit Ihrer Zustimmung geladen.
              Details finden Sie in unserer{" "}
              <Link to="/datenschutz" className="underline text-[#C9B79C]">Datenschutzerklärung</Link>.
            </p>

            {settingsOpen && (
              <div data-testid="cookie-settings-panel" className="mt-4 space-y-3 border border-white/15 p-4">
                <label className="flex items-center justify-between gap-4 text-sm">
                  <span><strong>Notwendig</strong> — erforderlich für den Betrieb der Website</span>
                  <input type="checkbox" checked disabled className="h-4 w-4 accent-[#B5651D]" />
                </label>
                <label className="flex items-center justify-between gap-4 text-sm cursor-pointer">
                  <span><strong>Externe Medien</strong> — z. B. Google Maps</span>
                  <input
                    type="checkbox"
                    data-testid="cookie-toggle-external"
                    checked={external}
                    onChange={(e) => setExternal(e.target.checked)}
                    className="h-4 w-4 accent-[#B5651D]"
                  />
                </label>
                <label className="flex items-center justify-between gap-4 text-sm cursor-pointer">
                  <span><strong>Statistik</strong> — anonyme Nutzungsanalyse</span>
                  <input
                    type="checkbox"
                    data-testid="cookie-toggle-statistics"
                    checked={statistics}
                    onChange={(e) => setStatistics(e.target.checked)}
                    className="h-4 w-4 accent-[#B5651D]"
                  />
                </label>
              </div>
            )}

            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="cookie-accept-all"
                onClick={() => save({ necessary: true, external: true, statistics: true })}
                className="bg-[#B5651D] text-white px-6 py-3 text-xs uppercase tracking-wider font-semibold transition-colors hover:bg-[#9c5517]"
              >
                Alle akzeptieren
              </button>
              <button
                data-testid="cookie-necessary-only"
                onClick={() => save({ necessary: true, external: false, statistics: false })}
                className="border border-white/50 text-white px-6 py-3 text-xs uppercase tracking-wider font-semibold transition-colors hover:bg-white hover:text-[#1F2933]"
              >
                Nur notwendige
              </button>
              {settingsOpen ? (
                <button
                  data-testid="cookie-save-settings"
                  onClick={() => save({ necessary: true, external, statistics })}
                  className="border border-[#C9B79C] text-[#C9B79C] px-6 py-3 text-xs uppercase tracking-wider font-semibold transition-colors hover:bg-[#C9B79C] hover:text-[#1F2933]"
                >
                  Auswahl speichern
                </button>
              ) : (
                <button
                  data-testid="cookie-open-settings"
                  onClick={() => setSettingsOpen(true)}
                  className="text-white/70 px-6 py-3 text-xs uppercase tracking-wider font-semibold underline transition-colors hover:text-white"
                >
                  Einstellungen
                </button>
              )}
            </div>
          </div>
          <button
            data-testid="cookie-close"
            onClick={() => save({ necessary: true, external: false, statistics: false })}
            aria-label="Banner schließen (nur notwendige Cookies)"
            className="text-white/50 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};
